/**
 * A bandeja de avisos do Portal (11.1/11.2).
 *
 * ## O que ela substitui
 *
 * O portal só sabia avisar em dois lugares fixos: um parágrafo colado no seletor de
 * período e um bloco vermelho dentro da lista de documentos. Isso obriga todo aviso a
 * nascer perto de onde o ato aconteceu — e os dois atos que mais precisam de resposta
 * (trocar a empresa representada e pedir o detalhe de uma linha) ou mudam a página
 * inteira, ou têm origem numa célula de tabela onde não cabe um parágrafo.
 *
 * ## A região viva é PERMANENTE
 *
 * Um `aria-live` que nasce junto com o texto não é anunciado: o leitor de tela precisa
 * estar observando a região antes de a frase aparecer nela. Por isso a região é a
 * própria bandeja, que existe desde a montagem. Erro leva `role="alert"`, que
 * interrompe; o resto é polido, porque sucesso que corta a fala é grosseria.
 *
 * ## Seis segundos contam para quem lê
 *
 * O prazo congela com o ponteiro em cima ou o foco dentro, e retoma de onde parou.
 * Sem isso, quem usa teclado ou lupa leva o cursor até o botão de fechar e ele some
 * debaixo dele.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { CircleCheck, CircleX, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react'

export type TomDoAviso = 'sucesso' | 'erro' | 'atencao' | 'info'

export interface PedidoDeAviso {
  readonly tom: TomDoAviso
  readonly titulo: string
  readonly texto?: string | undefined
  /** Marca de teste do aviso, quando o caso precisa achar ESTE e não a bandeja. */
  readonly teste?: string | undefined
}

interface AvisoNaTela extends PedidoDeAviso {
  readonly id: number
}

const Contexto = createContext<(pedido: PedidoDeAviso) => void>(() => {
  // Fora do provedor não há bandeja. Silêncio é melhor que exceção num aviso: o ato
  // que o disparou já aconteceu, e não é ele que está errado.
})

export function useAviso(): (pedido: PedidoDeAviso) => void {
  return useContext(Contexto)
}

const ContextoDeLimpeza = createContext<() => void>(() => { /* Sem provedor não há avisos a limpar. */ })
export function useLimparAvisos(): () => void { return useContext(ContextoDeLimpeza) }

const ICONE: Readonly<Record<TomDoAviso, LucideIcon>> = {
  sucesso: CircleCheck,
  erro: CircleX,
  atencao: TriangleAlert,
  info: Info,
}

/** Seis segundos: tempo de ler duas linhas sem virar mobília. */
const DURACAO_MS = 6000

interface Prazo {
  temporizador: ReturnType<typeof setTimeout>
  expiraEm: number
  restante: number
}

export function ProvedorDeAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<readonly AvisoNaTela[]>([])
  const prazos = useRef(new Map<number, Prazo>())
  const proximoId = useRef(0)

  const limpar = useCallback(() => {
    for (const prazo of prazos.current.values()) clearTimeout(prazo.temporizador)
    prazos.current.clear()
    setAvisos([])
  }, [])

  const fechar = useCallback((id: number) => {
    const prazo = prazos.current.get(id)
    if (prazo !== undefined) {
      clearTimeout(prazo.temporizador)
      prazos.current.delete(id)
    }
    setAvisos((atuais) => atuais.filter((aviso) => aviso.id !== id))
  }, [])

  const agendar = useCallback(
    (id: number, ms: number) => {
      prazos.current.set(id, {
        temporizador: setTimeout(() => {
          fechar(id)
        }, ms),
        expiraEm: Date.now() + ms,
        restante: ms,
      })
    },
    [fechar],
  )

  /** Congela o prazo de todos: o ponteiro está em cima, ou o foco está dentro. */
  const pausar = useCallback(() => {
    for (const [id, prazo] of prazos.current) {
      clearTimeout(prazo.temporizador)
      prazos.current.set(id, { ...prazo, restante: Math.max(0, prazo.expiraEm - Date.now()) })
    }
  }, [])

  /** Retoma de onde parou. Reiniciar faria o ponteiro parado virar aviso eterno. */
  const retomar = useCallback(() => {
    for (const [id, prazo] of [...prazos.current]) agendar(id, prazo.restante)
  }, [agendar])

  const mostrar = useCallback(
    (pedido: PedidoDeAviso) => {
      // Contador monotônico: `Date.now()` colide quando um ato dispara dois avisos no
      // mesmo milissegundo, que é justamente quando os dois precisam conviver.
      proximoId.current += 1
      const id = proximoId.current
      setAvisos((atuais) => [...atuais, { ...pedido, id }])
      agendar(id, DURACAO_MS)
    },
    [agendar],
  )

  // Nenhum temporizador sobrevive à desmontagem do provedor.
  useEffect(() => {
    const abertos = prazos.current
    return () => {
      for (const prazo of abertos.values()) clearTimeout(prazo.temporizador)
      abertos.clear()
    }
  }, [])

  const valor = useMemo(() => mostrar, [mostrar])

  return (
    <Contexto.Provider value={valor}>
      <ContextoDeLimpeza.Provider value={limpar}>
      {children}
      <div
        className="avisos"
        data-teste="avisos"
        aria-live="polite"
        aria-atomic="false"
        onMouseEnter={pausar}
        onMouseLeave={retomar}
        onFocusCapture={pausar}
        onBlurCapture={retomar}
      >
        {avisos.map((aviso) => {
          const Icone = ICONE[aviso.tom]
          return (
            <div
              key={aviso.id}
              className={`aviso ${aviso.tom}`}
              {...(aviso.tom === 'erro' ? { role: 'alert' } : {})}
              {...(aviso.teste === undefined ? {} : { 'data-teste': aviso.teste })}
            >
              <Icone size={18} aria-hidden="true" />
              <span>
                <strong>{aviso.titulo}</strong>
                {aviso.texto !== undefined && aviso.texto !== '' && <small>{aviso.texto}</small>}
              </span>
              <button
                type="button"
                aria-label="Fechar"
                onClick={() => {
                  fechar(aviso.id)
                }}
              >
                <X size={16} />
              </button>
            </div>
          )
        })}
      </div>
    </ContextoDeLimpeza.Provider>
    </Contexto.Provider>
  )
}
